// Tracks autosave state per form for forms using `data-controller~="auto-submit"`.
//
// Data attributes (on <form>):
// - data-autosave-pending-count="0|1|..."
// - data-autosave-scheduled="true|false"   (autosave scheduled by input but not yet submitted)
// - data-autosave-dirty="true|false"       (submit attempted while another is in-flight)
// - data-autosave-busy="true|false"        (derived: pendingCount>0 || scheduled || dirty)

const AUTO_SUBMIT_SELECTOR = 'form[data-controller~="auto-submit"]';
let installed = false;

function isAutoSubmitForm(el: Element | null): el is HTMLFormElement {
  return el instanceof HTMLFormElement && el.matches(AUTO_SUBMIT_SELECTOR);
}

function pendingCount(form: HTMLFormElement): number {
  const n = Number(form.dataset.autosavePendingCount);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function updateBusy(form: HTMLFormElement) {
  const pending = pendingCount(form) > 0;
  const scheduled = form.dataset.autosaveScheduled === "true";
  const dirty = form.dataset.autosaveDirty === "true";
  form.dataset.autosaveBusy = pending || scheduled || dirty ? "true" : "false";
}

function initForm(form: HTMLFormElement) {
  form.dataset.autosavePendingCount ||= "0";
  form.dataset.autosaveScheduled ||= "false";
  form.dataset.autosaveDirty ||= "false";
  updateBusy(form);
}

function initAllAutoSubmitForms() {
  document
    .querySelectorAll<HTMLFormElement>(AUTO_SUBMIT_SELECTOR)
    .forEach(initForm);
}

function onUserInput(event: Event) {
  const target = event.target as Element | null;
  const form = target?.closest(AUTO_SUBMIT_SELECTOR) ?? null;
  if (!isAutoSubmitForm(form)) return;

  initForm(form);
  form.dataset.autosaveScheduled = "true";
  updateBusy(form);
}

function onSubmitCapture(event: Event) {
  const form = event.target as Element | null;
  if (!isAutoSubmitForm(form)) return;

  initForm(form);

  const pending = pendingCount(form);
  if (pending > 0) {
    // Serialize: don't allow concurrent submissions. Mark dirty so we flush after the in-flight submit ends.
    form.dataset.autosaveDirty = "true";
    event.preventDefault();
    event.stopImmediatePropagation();
  } else {
    // A submission is starting now (requestSubmit() already happened).
    form.dataset.autosaveScheduled = "false";
    form.dataset.autosavePendingCount = String(pending + 1);
  }
  updateBusy(form);
}

function onSubmitEnd(event: Event) {
  const form = event.target as Element | null;
  if (!isAutoSubmitForm(form)) return;

  initForm(form);

  const pending = pendingCount(form);
  const nextPending = Math.max(0, pending - 1);
  form.dataset.autosavePendingCount = String(nextPending);

  // If anything changed while submit was in-flight, flush once more.
  if (
    nextPending === 0 &&
    (form.dataset.autosaveDirty === "true" ||
      form.dataset.autosaveScheduled === "true")
  ) {
    form.dataset.autosaveDirty = "false";
    form.dataset.autosaveScheduled = "false";
    updateBusy(form);
    form.requestSubmit();
    return;
  }

  updateBusy(form);
}

export function installAutosaveStatusTracking() {
  if (installed) return;
  installed = true;

  initAllAutoSubmitForms();
  document.addEventListener("turbo:load", initAllAutoSubmitForms);
  document.addEventListener("turbo:render", initAllAutoSubmitForms);

  document.addEventListener("change", onUserInput, true);
  document.addEventListener("input", onUserInput, true);
  document.addEventListener("keyup", onUserInput, true);

  document.addEventListener("submit", onSubmitCapture, true);
  document.addEventListener("turbo:submit-end", onSubmitEnd as EventListener);
}

installAutosaveStatusTracking();
